/**
 * Access request rejection page.
 *
 * The profile owner clicks "Reject" in their notification email and lands here.
 * Token from the email is the proof of identity.
 * Public route — no authentication required.
 */

import { useRejectAccessRequestMutation } from "@/lib/queries/projects";
import {
  Button,
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, Loader2, X } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

const searchSchema = z.object({
  token: z.string().min(1, "Token is required"),
});

export const Route = createFileRoute("/access-requests/reject")({
  validateSearch: searchSchema,
  component: RejectAccessRequestPage,
});

export function RejectAccessRequestPage() {
  const { token } = Route.useSearch();
  const mutation = useRejectAccessRequestMutation();

  useEffect(() => {
    mutation.mutate({ token });
    // Run once on mount
  }, [token]);

  if (mutation.isPending || mutation.isIdle) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
            <CardTitle>Rejecting request...</CardTitle>
            <CardDescription>Please wait a moment.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (mutation.isError) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Something went wrong</CardTitle>
            <CardDescription>
              {(mutation.error as Error)?.message ??
                "Could not reject the request. Please try again."}
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/">
              <Button variant="outline">Go to Dashboard</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <X className="h-6 w-6 text-muted-foreground" />
          </div>
          <CardTitle>Request Rejected</CardTitle>
          <CardDescription>
            The access request has been rejected. The requestor will not receive
            an invitation.
          </CardDescription>
        </CardHeader>
        <CardFooter className="justify-center">
          <Link to="/">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
}
